/**
 * Month calendar (spec 2026-10-14 §9): "October 2026" on the left with ‹ › on the right, S–S
 * initials, then 4–6 Sunday-start week rows from getMonthMatrix. Each day is a DayCell — blue only for
 * today, the primary ink for another picked day, one quiet dot where work is left. Adjacent-month
 * cells quiet their number but stay tappable, and dots render on them too.
 */
import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTypography } from '@/hooks/shared/useTypography';
import { useSpacing } from '@/hooks/shared/useSpacing';
import { useRadius } from '@/hooks/shared/useRadius';
import {
  formatMonthLabel,
  getMonthMatrix,
  isSameMonth,
  toDateKey,
} from '@/utils/residents/todoCalendar';
import { DayCell } from './DayCell';

const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
/** The ‹ › glyphs. Their box is the 44pt target. */
const ARROW_SIZE = 18;

interface MonthGridProps {
  /** Any date inside the visible month. */
  monthAnchor: Date;
  selectedKey: string;
  todayKey: string;
  /**
   * Remaining (incomplete) todos per date key — what the dot and the cell's
   * a11y label both stand for. The home widget's WeekStrip is fed the same
   * filtered counts, so the two calendars agree about a date.
   */
  countsByKey: Record<string, number>;
  onSelectDay: (d: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

export const MonthGrid: React.FC<MonthGridProps> = React.memo(
  ({ monthAnchor, selectedKey, todayKey, countsByKey, onSelectDay, onPrevMonth, onNextMonth }) => {
    const type = useTypography();
    const space = useSpacing();
    const radius = useRadius();
    const styles = useThemedStyles(({ colors }) =>
      StyleSheet.create({
        container: {
          gap: space.sm,
        },
        headerRow: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        },
        monthLabel: {
          ...type.sectionTitle,
          color: colors.text.primary,
        },
        // Pulled out by one arrow's inset so the › glyph lines up with the grid's right edge.
        arrows: {
          flexDirection: 'row',
          marginRight: -space.md,
        },
        arrow: {
          minHeight: 44,
          minWidth: 44,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: radius.sm,
        },
        arrowIcon: {
          color: colors.text.quiet,
        },
        weekdaysRow: {
          flexDirection: 'row',
        },
        weekdayLabel: {
          flex: 1,
          textAlign: 'center',
          ...type.captionMedium,
          color: colors.text.quiet,
        },
        weekRow: {
          flexDirection: 'row',
        },
      }),
    );

    const weeks = useMemo(() => getMonthMatrix(monthAnchor), [monthAnchor]);

    return (
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <Text style={styles.monthLabel}>{formatMonthLabel(monthAnchor)}</Text>
          <View style={styles.arrows}>
            <Hoverable
              hover="row"
              style={styles.arrow}
              onPress={onPrevMonth}
              accessibilityRole="button"
              accessibilityLabel="Previous month"
            >
              <Icon name="chevron-back" size={ARROW_SIZE} style={styles.arrowIcon} />
            </Hoverable>
            <Hoverable
              hover="row"
              style={styles.arrow}
              onPress={onNextMonth}
              accessibilityRole="button"
              accessibilityLabel="Next month"
            >
              <Icon name="chevron-forward" size={ARROW_SIZE} style={styles.arrowIcon} />
            </Hoverable>
          </View>
        </View>
        {/* Sight only: each day cell's label names its weekday, so these would be seven one-letter stops. */}
        <View
          style={styles.weekdaysRow}
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          aria-hidden
        >
          {WEEKDAY_INITIALS.map((initial, index) => (
            <Text key={index} scale="micro" style={styles.weekdayLabel}>
              {initial}
            </Text>
          ))}
        </View>
        {weeks.map(week => (
          <View key={toDateKey(week[0])} style={styles.weekRow}>
            {week.map(day => {
              const key = toDateKey(day);
              return (
                <DayCell
                  key={key}
                  day={day}
                  isToday={key === todayKey}
                  isSelected={key === selectedKey}
                  isOutside={!isSameMonth(day, monthAnchor)}
                  count={countsByKey[key] ?? 0}
                  onPress={onSelectDay}
                />
              );
            })}
          </View>
        ))}
      </View>
    );
  },
);

MonthGrid.displayName = 'MonthGrid';
